import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import SparesDispatchDialog from "@/components/SparesDispatchDialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { authImg } from "@/lib/authImg";
import { Send, ImageOff } from "lucide-react";

const PNA_OPTIONS = [
  { value: "fresh_unit", label: "Take out from fresh unit" },
  { value: "damaged_set", label: "Take out from damaged set" },
  { value: "refurbished_set", label: "Take out from refurbished set" },
  { value: "market_purchase", label: "Purchase from market" },
];

export default function PartsRequests() {
  const { user, can } = useAuth();
  const [tab, setTab] = useState("Parts Pending");
  const [list, setList] = useState([]);

  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";
  const isHead = user?.role === "service_head";
  const isSpares = user?.role === "spares";

  const canApproveNormal = can?.("parts_inventory.approve") || isAdmin || isManager;
  const canMarkPNA = can?.("parts_inventory.mark_pna") || isAdmin || isSpares;
  const canApprovePNASource = can?.("parts_inventory.approve_pna_source") || isAdmin || isHead;
  const canDispatch = can?.("parts_inventory.dispatch") || isAdmin || isSpares;

  const load = async () => {
    const { data } = await api.get("/complaints", { params: { status: tab } });
    setList(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  // ---- Approve normal parts ----
  const approve = async (c) => {
    const remarks = isAdmin ? (window.prompt("Admin override: enter approval remarks") || "") : "";
    if (isAdmin && !remarks.trim()) {
      toast.error("Admin override requires remarks");
      return;
    }
    try {
      await api.post(`/complaints/${c.id}/approve-parts`, { remarks });
      toast.success("Parts approved");
      load();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  // ---- Mark PNA (Spares) ----
  const [pnaOpen, setPnaOpen] = useState(false);
  const [pnaTarget, setPnaTarget] = useState(null);
  const [pnaForm, setPnaForm] = useState({ pna_part_details: "", remarks: "" });
  const openMarkPNA = (c) => {
    setPnaTarget(c);
    setPnaForm({ pna_part_details: c.parts_required || "", remarks: "" });
    setPnaOpen(true);
  };
  const submitPNA = async () => {
    if (!pnaForm.pna_part_details.trim()) { toast.error("PNA part details required"); return; }
    try {
      await api.post(`/complaints/${pnaTarget.id}/mark-pna`, pnaForm);
      toast.success("Marked PNA");
      setPnaOpen(false);
      load();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  // ---- Approve PNA source (Service Head) ----
  const [srcOpen, setSrcOpen] = useState(false);
  const [srcTarget, setSrcTarget] = useState(null);
  const [srcForm, setSrcForm] = useState({ source: "fresh_unit", remarks: "" });
  const openApproveSource = (c) => {
    setSrcTarget(c);
    setSrcForm({ source: "fresh_unit", remarks: "" });
    setSrcOpen(true);
  };
  const submitSource = async () => {
    if (isAdmin && !srcForm.remarks.trim()) { toast.error("Admin override requires remarks"); return; }
    try {
      await api.post(`/complaints/${srcTarget.id}/approve-pna-source`, srcForm);
      toast.success(`PNA source approved (${srcForm.source})`);
      setSrcOpen(false);
      load();
    } catch (e) {
      toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) });
    }
  };

  // ---- Dispatch (Spares / Admin) ----
  const [dispatchOpen, setDispatchOpen] = useState(false);
  const [dispatchTarget, setDispatchTarget] = useState(null);
  const openDispatch = (c) => { setDispatchTarget(c); setDispatchOpen(true); };

  const tabs = ["Parts Pending", "PNA", "Parts Approved", "Parts in Transit"];
  const tabLabels = {
    "Parts Pending": "Parts Pending",
    "PNA": "PNA",
    "Parts Approved": "Approved Parts Pending Dispatch",
    "Parts in Transit": "Parts in Transit (Dispatched)",
  };

  return (
    <div className="space-y-5" data-testid="parts-requests-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Parts Requests</h1>
        <p className="text-sm text-slate-500 mt-1">
          Service Manager approves normal parts. Spares marks PNA. Service Head approves PNA source.
        </p>
      </div>
      <div className="flex gap-2 flex-wrap">
        {tabs.map((t) => (
          <Button key={t} variant={tab === t ? "default" : "outline"} className={tab === t ? "bg-slate-900 text-white" : ""}
                  onClick={() => setTab(t)} data-testid={`pr-tab-${t.replaceAll(" ", "-")}`}>
            {tabLabels[t]}
          </Button>
        ))}
      </div>
      {tab === "Parts Approved" && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="approved-pending-dispatch-hint">
          These approved part requests are waiting to be dispatched to the technician. Click <b>Dispatch</b> to confirm POD, attach the spare photo and courier details.
        </div>
      )}
      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full dense-table">
            <thead className="bg-slate-50 border-b"><tr>
              <Th>Complaint #</Th><Th>Brand / Product</Th>
              <Th>Parts Required</Th><Th>PNA Details</Th>
              <Th>Technician</Th><Th>Status</Th>
              <Th align="right">Actions</Th>
            </tr></thead>
            <tbody>
              {list.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-slate-400">No items.</td></tr>}
              {list.map((c) => {
                const showApproveNormal = c.status === "Parts Pending" && canApproveNormal;
                const showMarkPNA = c.status === "Parts Pending" && canMarkPNA;
                const showApprovePNASource = c.status === "PNA" && canApprovePNASource;
                const showDispatch = c.status === "Parts Approved" && canDispatch;
                return (
                  <tr key={c.id} data-testid={`pr-row-${c.complaint_number}`}>
                    <td><Link className="font-mono hover:underline" to={`/complaints/${c.id}`}>{c.complaint_number}</Link></td>
                    <td>{c.brand}<div className="text-xs text-slate-500">{c.product_name}</div></td>
                    <td className="text-slate-700">
                      <div className="flex items-start gap-2">
                        {(c.parts_request?.damaged_spare_photo_url || c.damaged_spare_photo_url) && (
                          <PartsDamagedThumb url={c.parts_request?.damaged_spare_photo_url || c.damaged_spare_photo_url}
                                             testId={`pr-damaged-photo-${c.complaint_number}`} />
                        )}
                        <div className="min-w-0">
                          <div>{c.parts_required || c.parts_request?.description || "—"}</div>
                          {(c.parts_request?.part_name || c.parts_request?.part_code) && (
                            <div className="text-xs text-slate-500">
                              {c.parts_request?.part_name}
                              {c.parts_request?.part_code && <span className="font-mono"> ({c.parts_request.part_code})</span>}
                              {c.parts_request?.quantity && <> · qty {c.parts_request.quantity}</>}
                              {c.parts_request?.urgency && c.parts_request.urgency !== "normal" && (
                                <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 uppercase font-semibold">
                                  {c.parts_request.urgency}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="text-slate-700">
                      {c.pna_part_details || "—"}
                      {c.pna_source && (
                        <div className="text-xs text-emerald-700 mt-0.5">
                          Source: <b>{c.pna_source.replaceAll("_", " ")}</b>
                          {c.pna_source_approved_by && <span className="text-slate-500"> · by {c.pna_source_approved_by}</span>}
                        </div>
                      )}
                    </td>
                    <td>{c.technician_name || "—"}</td>
                    <td><StatusBadge status={c.status} /></td>
                    <td className="text-right space-x-1">
                      {showApproveNormal && (
                        <Button size="sm" onClick={() => approve(c)} data-testid={`approve-${c.id}`} className="bg-slate-900 text-white">Approve</Button>
                      )}
                      {showMarkPNA && (
                        <Button size="sm" variant="outline" onClick={() => openMarkPNA(c)} data-testid={`mark-pna-${c.id}`}>Mark PNA</Button>
                      )}
                      {showApprovePNASource && (
                        <Button size="sm" onClick={() => openApproveSource(c)} data-testid={`approve-pna-source-${c.id}`} className="bg-amber-600 text-white hover:bg-amber-700">Approve PNA Source</Button>
                      )}
                      {showDispatch && (
                        <Button size="sm" onClick={() => openDispatch(c)} data-testid={`dispatch-${c.id}`} className="bg-emerald-600 text-white hover:bg-emerald-700">
                          <Send size={12} className="mr-1" /> Dispatch
                        </Button>
                      )}
                      <Link to={`/complaints/${c.id}`}><Button size="sm" variant="outline">Open</Button></Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {/* Mark PNA dialog (Spares) */}
      <Dialog open={pnaOpen} onOpenChange={setPnaOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Mark Part as PNA · {pnaTarget?.complaint_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Fld label="PNA Part Details" required>
              <Input value={pnaForm.pna_part_details} onChange={(e) => setPnaForm({ ...pnaForm, pna_part_details: e.target.value })} data-testid="pna-details-input" />
            </Fld>
            <Fld label="Remarks">
              <Input value={pnaForm.remarks} onChange={(e) => setPnaForm({ ...pnaForm, remarks: e.target.value })} data-testid="pna-remarks-input" />
            </Fld>
            <div className="text-xs text-slate-500">After marking PNA, Service Head must approve a source (fresh / damaged / refurbished / market).</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPnaOpen(false)}>Cancel</Button>
            <Button onClick={submitPNA} className="bg-slate-900 text-white" data-testid="submit-mark-pna">Mark PNA</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve PNA source dialog (Service Head) */}
      <Dialog open={srcOpen} onOpenChange={setSrcOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Approve PNA Source · {srcTarget?.complaint_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Fld label="Source Decision" required>
              <select className="border border-slate-200 rounded-md w-full h-9 px-2 text-sm" value={srcForm.source} onChange={(e) => setSrcForm({ ...srcForm, source: e.target.value })} data-testid="pna-source-select">
                {PNA_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Fld>
            <Fld label={isAdmin ? "Remarks (required for admin override)" : "Remarks"} required={isAdmin}>
              <Input value={srcForm.remarks} onChange={(e) => setSrcForm({ ...srcForm, remarks: e.target.value })} data-testid="pna-source-remarks-input" />
            </Fld>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSrcOpen(false)}>Cancel</Button>
            <Button onClick={submitSource} className="bg-amber-600 text-white hover:bg-amber-700" data-testid="submit-approve-pna-source">Approve Source</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Spares dispatch dialog (Spares / Admin) */}
      <SparesDispatchDialog
        open={dispatchOpen}
        onOpenChange={setDispatchOpen}
        complaint={dispatchTarget}
        onDispatched={() => { setDispatchTarget(null); load(); }}
      />
    </div>
  );
}

function Fld({ label, children, required }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
        {label}{required && <span className="text-rose-600 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}
function Th({ children, align }) {
  return <th className={`${align === "right" ? "text-right" : "text-left"} text-xs uppercase tracking-wide text-slate-600 font-semibold`}>{children}</th>;
}


function PartsDamagedThumb({ url, testId }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [url]);
  const full = authImg(url);
  if (broken) {
    return (
      <div className="h-10 w-10 rounded border border-dashed border-slate-300 flex items-center justify-center text-slate-400"
           data-testid={`${testId}-missing`} title="Photo not available">
        <ImageOff size={12} />
      </div>
    );
  }
  return (
    <a href={full} target="_blank" rel="noopener noreferrer" data-testid={testId}>
      <img src={full} alt="damaged"
           className="h-10 w-10 object-cover rounded border border-slate-200"
           onError={() => setBroken(true)} />
    </a>
  );
}
